import { ExitIcon } from '@/common/icons/ExitIcon';
import { LogoDigitAgroIcon } from '@/common/icons/LogoDigitAgro';
import { MoniLogo } from '@/common/icons/MoniLogo';
import { useSidebar } from '@/constants/sidebar';
import { cn } from '@/lib/utils';
import useSidebarStore from '@/store/sidebar';
import { logout } from '@/utils/user';
import { ChevronLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { SidebarProps } from '@/common/models/ISidebar';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { useUserInfo } from '@/hooks/useUserInfo';

export const Sidebar = () => {
  const { t } = useTranslation();
  const { SIDEBAR } = useSidebar();
  const { isOpenSidebar, setIsOpenSidebar } = useSidebarStore();
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState(
    () => location.pathname.split('/')[1] || '',
  );
  useUserInfo();
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 1440) {
        setIsOpenSidebar(true);
      } else {
        setIsOpenSidebar(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => {
    setIsOpenSidebar(!isOpenSidebar);
  };

  const labelClass = cn(
    'overflow-hidden whitespace-nowrap text-sm font-medium text-white/70',
    'transition-[max-width,opacity,margin] duration-300 ease-in-out',
    isOpenSidebar ? 'ml-0 max-w-0 opacity-0' : 'ml-2 max-w-[180px] opacity-100',
  );

  const getNavigationSearch = (pathname: string) => {
    const target = pathname.startsWith('/') ? pathname : `/${pathname}`;
    const isSamePage = location.pathname === target;

    const isAgrosubsidiya = location.pathname.endsWith('/agrosubsidiya');
    const isAgrosubsidiyaTarget = pathname.endsWith('/agrosubsidiya');
    const isEnteringAgrosubsidiya = !isAgrosubsidiya && isAgrosubsidiyaTarget;
    const isLeavingAgrosubsidiya = isAgrosubsidiya && !isAgrosubsidiyaTarget;

    if (isSamePage && !isEnteringAgrosubsidiya && !isLeavingAgrosubsidiya) {
      return location.search;
    }

    const params = new URLSearchParams(location.search);
    /** Sahifaga xos tanlovlar boshqa sahifaga ergashib ketmasin.
     *  `year` va `plant_type` — umumiy filtrlar, ular saqlanadi. */
    params.delete('region');
    params.delete('tax_number');
    params.delete('page');
    params.delete('size');
    params.delete('page_size');
    params.delete('subsidiya_region');
    params.delete('region_code');

    return params.toString();
  };

  const renderLink = (item: SidebarProps) => (
    <NavLink
      key={item.id}
      to={{ pathname: item.href, search: getNavigationSearch(item.href) }}
      className={({ isActive }) =>
        cn(
          'flex items-center rounded-[6px] p-1.5 transition-all duration-300',
          isActive ? `${item.bgColor} bg-white-14` : '',
          isOpenSidebar ? 'w-fit' : 'w-full',
        )
      }
    >
      {({ isActive }) => (
        <>
          <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center">
            <item.Icon
              className={cn('h-6 w-6 text-white/70', isActive && 'text-white')}
            />
          </div>
          <span className={cn(labelClass, isActive && 'text-white')}>
            {t(item.title)}
          </span>
        </>
      )}
    </NavLink>
  );

  return (
    <div
      className={cn(
        'relative h-full flex-shrink-0 transition-[width,min-width,max-width] duration-300 ease-in-out',
        isOpenSidebar
          ? 'w-[80px] min-w-[80px] max-w-[80px]'
          : 'w-[279px] min-w-[279px] max-w-[279px]',
      )}
    >
      <div
        className="absolute right-[-12px] top-[84px] z-50 flex h-12 w-3 cursor-pointer items-center justify-center rounded-br-sm rounded-tr-sm bg-indigo-accent"
        onClick={toggleSidebar}
      >
        <ChevronLeft
          className={cn(
            'h-3 w-3 text-white transition-transform duration-300',
            isOpenSidebar && 'rotate-180',
          )}
        />
      </div>

      <div className="h-full w-full overflow-hidden bg-card shadow-light">
        <div className="flex h-full min-h-0 flex-col justify-between">
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex shrink-0 items-center gap-2 p-4">
              <div className="flex h-9 w-11 flex-shrink-0 items-center justify-center">
                <MoniLogo className="h-9 w-11" />
              </div>
              <h1
                className={cn(
                  'text-md overflow-hidden whitespace-nowrap font-medium text-white',
                  'transition-[max-width,opacity] duration-300 ease-in-out',
                  isOpenSidebar
                    ? 'max-w-0 opacity-0'
                    : 'max-w-[180px] opacity-100',
                )}
              >
                {t('main_title')}
              </h1>
            </div>

            {/* Menyu uzayganda ro'yxatning o'zi scroll bo'ladi — pastdagi
                chiqish tugmasi va banner har doim ko'rinib turadi. */}
            <div className="sidebar-scrollbar flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-4">
              {SIDEBAR.map((item) =>
                item.childrens?.length ? (
                  <div key={item.id} className="flex flex-col">
                    <div
                      className={cn(
                        'flex cursor-pointer items-center rounded-[6px] p-1.5 transition-all duration-300',
                        isOpenSidebar ? 'w-fit' : 'w-full',
                      )}
                      onClick={() =>
                        setOpenMenu(openMenu === item.id ? '' : item.id)
                      }
                    >
                      <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center">
                        <item.Icon className="h-6 w-6 text-white/70" />
                      </div>
                      <span className={labelClass}>{t(item.title)}</span>
                      {!isOpenSidebar && (
                        <ChevronLeft
                          className={cn(
                            'ml-auto h-4 w-4 text-white/70 transition-transform duration-300',
                            openMenu === item.id && '-rotate-90',
                          )}
                        />
                      )}
                    </div>
                    {/* grid-rows 0fr -> 1fr: balandlikni bilmasdan silliq ochiladi. */}
                    <div
                      className={cn(
                        'grid transition-[grid-template-rows] duration-300 ease-in-out',
                        openMenu === item.id
                          ? 'grid-rows-[1fr]'
                          : 'grid-rows-[0fr]',
                      )}
                    >
                      <div className="overflow-hidden">
                        <div
                          className={cn(
                            'flex flex-col gap-2 pt-2',
                            !isOpenSidebar && 'pl-4',
                          )}
                        >
                          {item.childrens.map(renderLink)}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  renderLink(item)
                ),
              )}
            </div>
          </div>

          <div className="flex w-full shrink-0 flex-col gap-5 p-4">
            <div
              className={cn(
                'flex flex-col gap-2.5 rounded-sm border border-[rgba(255,255,255,0.10)] bg-[rgba(255,255,255,0.04)]',
                'overflow-hidden transition-[max-height,opacity,padding,border-width] duration-300 ease-in-out',
                isOpenSidebar
                  ? 'hidden max-h-0 border-0 p-0 opacity-0'
                  : 'max-h-[200px] p-3 opacity-100',
              )}
              onClick={() => window.open('https://digitagro.uz/', '_blank')}
            >
              <LogoDigitAgroIcon />
              <span className="text-sm font-normal text-white/70">
                {t('agro_center_info')}
              </span>
              <Button
                variant="outline"
                onClick={() => window.open('https://digitagro.uz/', '_blank')}
                className="h-7 w-full rounded-sm border border-[rgba(255,255,255,0.10)] bg-[rgba(255,255,255,0.10)] text-sm text-white/70 hover:bg-[rgba(255,255,255,0.10)]"
              >
                {t('more_info')}
              </Button>
            </div>

            <div
              className="flex w-full cursor-pointer items-center rounded-[6px] p-1.5 transition-colors hover:bg-white-14"
              onClick={logout}
            >
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center">
                <ExitIcon className="h-6 w-6 text-white/70" />
              </div>
              <span
                className={cn(
                  'overflow-hidden whitespace-nowrap text-sm font-medium text-white/70',
                  'transition-[max-width,opacity,margin] duration-300 ease-in-out',
                  isOpenSidebar
                    ? 'ml-0 max-w-0 opacity-0'
                    : 'ml-2 max-w-[180px] opacity-100',
                )}
              >
                {t('logout')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
